// Servicio de Configuración de Ayuda
// Maneja las operaciones CRUD para la configuración de la página de ayuda

import { 
  getDocument, 
  updateDocument 
} from './firestore.service'
import { doc, setDoc, Timestamp } from 'firebase/firestore'
import { db } from './firebase.config'

const COLLECTION = 'helpSettings'
const DOCUMENT_ID = 'settings' // Un solo documento con toda la configuración

/**
 * Estructura del documento:
 * {
 *   faqs: [
 *     {
 *       id: string,
 *       question: string,
 *       answer: string (HTML),
 *       order: number
 *     }
 *   ],
 *   shippingInfo: string (HTML),
 *   returnsInfo: string (HTML),
 *   warrantyInfo: string (HTML),
 *   paymentMethods: string (HTML),
 *   contact: {
 *     address: string,
 *     phone: string,
 *     email: string,
 *     schedule: string,
 *     socialMedia: {
 *       facebook: string (URL),
 *       instagram: string (URL),
 *       whatsapp: string (número sin + ni espacios)
 *     }
 *   },
 *   updatedAt: Timestamp
 * }
 */

/**
 * Obtener la configuración de ayuda
 * @returns {Promise<Object>} Configuración de ayuda
 */
export const getHelpSettings = async () => {
  try {
    const settings = await getDocument(COLLECTION, DOCUMENT_ID)
    
    // Si no existe, retornar estructura por defecto
    if (!settings) {
      return {
        id: DOCUMENT_ID,
        faqs: [],
        shippingInfo: '',
        returnsInfo: '',
        warrantyInfo: '',
        paymentMethods: '',
        contact: {
          address: '',
          phone: '',
          email: '',
          schedule: '',
          socialMedia: {
            facebook: '',
            instagram: '',
            whatsapp: ''
          }
        },
        updatedAt: null
      }
    }
    
    // Asegurar que contact existe con estructura completa
    if (!settings.contact) {
      settings.contact = {
        address: '',
        phone: '',
        email: '',
        schedule: '',
        socialMedia: {
          facebook: '',
          instagram: '',
          whatsapp: ''
        }
      }
    }
    
    // Asegurar que socialMedia existe
    if (!settings.contact.socialMedia) {
      settings.contact.socialMedia = {
        facebook: '',
        instagram: '',
        whatsapp: ''
      }
    }
    
    return settings
  } catch (error) {
    console.error('Error obteniendo configuración de ayuda:', error)
    throw error
  }
}

/**
 * Actualizar la configuración de ayuda
 * @param {Object} settingsData - Datos de configuración
 * @returns {Promise<void>}
 */
export const updateHelpSettings = async (settingsData) => {
  try {
    // Verificar si el documento existe
    const existing = await getDocument(COLLECTION, DOCUMENT_ID)
    
    const settingsDoc = {
      ...settingsData,
      updatedAt: Timestamp.now()
    }
    
    if (existing) {
      // Actualizar documento existente
      await updateDocument(COLLECTION, DOCUMENT_ID, settingsDoc)
    } else {
      // Crear nuevo documento con ID específico usando setDoc
      settingsDoc.createdAt = Timestamp.now()
      const docRef = doc(db, COLLECTION, DOCUMENT_ID)
      await setDoc(docRef, settingsDoc)
    }
  } catch (error) {
    console.error('Error actualizando configuración de ayuda:', error)
    throw error
  }
}

/**
 * Agregar una nueva FAQ
 * @param {Object} faqData - Datos de la FAQ { question, answer, order }
 * @returns {Promise<void>}
 */
export const addFAQ = async (faqData) => {
  try {
    const settings = await getHelpSettings()
    const newFAQ = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      question: faqData.question.trim(),
      answer: faqData.answer || '',
      order: faqData.order !== undefined ? faqData.order : (settings.faqs?.length || 0)
    }
    
    const updatedFAQs = [...(settings.faqs || []), newFAQ]
    
    await updateHelpSettings({
      ...settings,
      faqs: updatedFAQs
    })
    
    return newFAQ.id
  } catch (error) {
    console.error('Error agregando FAQ:', error)
    throw error
  }
}

/**
 * Actualizar una FAQ
 * @param {string} faqId - ID de la FAQ
 * @param {Object} faqData - Datos actualizados { question, answer, order }
 * @returns {Promise<void>}
 */
export const updateFAQ = async (faqId, faqData) => {
  try {
    const settings = await getHelpSettings()
    const updatedFAQs = (settings.faqs || []).map(faq => 
      faq.id === faqId 
        ? { ...faq, ...faqData, question: faqData.question?.trim() || faq.question }
        : faq
    )
    
    await updateHelpSettings({
      ...settings,
      faqs: updatedFAQs
    })
  } catch (error) {
    console.error('Error actualizando FAQ:', error)
    throw error
  }
}

/**
 * Eliminar una FAQ
 * @param {string} faqId - ID de la FAQ
 * @returns {Promise<void>}
 */
export const deleteFAQ = async (faqId) => {
  try {
    const settings = await getHelpSettings()
    const updatedFAQs = (settings.faqs || []).filter(faq => faq.id !== faqId)
    
    await updateHelpSettings({
      ...settings,
      faqs: updatedFAQs
    })
  } catch (error) {
    console.error('Error eliminando FAQ:', error)
    throw error
  }
}

/**
 * Reordenar FAQs
 * @param {Array} faqIds - Array de IDs en el nuevo orden
 * @returns {Promise<void>}
 */
export const reorderFAQs = async (faqIds) => {
  try {
    const settings = await getHelpSettings()
    const faqsMap = new Map((settings.faqs || []).map(faq => [faq.id, faq]))
    
    const reorderedFAQs = faqIds.map((id, index) => ({
      ...faqsMap.get(id),
      order: index
    }))
    
    await updateHelpSettings({
      ...settings,
      faqs: reorderedFAQs
    })
  } catch (error) {
    console.error('Error reordenando FAQs:', error)
    throw error
  }
}

export default {
  getHelpSettings,
  updateHelpSettings,
  addFAQ,
  updateFAQ,
  deleteFAQ,
  reorderFAQs
}

